import { useState, useEffect, useReducer, useContext, useRef } from "react";

import axios from "../../../interceptors/axiosInstance";
import { DatePicker } from "../../../components/General/DataEntry/DatePicker/DatePicker";
import { config } from "../../../config";
import { CountryList } from "../../../utils/CountryList";
import { CurrencyList } from "../../../utils/CurrencyList";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { LoaderContext } from "../../../context/LoaderContext";
import {
  decimalNumberRegex,
  negativeNumberRegex,
  whiteSpaceStringRegex,
  wholeNumberRegex,
  dateRegex,
} from "../../../utils/ValidationRules";
import { pascalCase } from "../../../utils/display";
import { MessageContext } from "../../../context/MessageContext";
import SuccessModal from "../../../components/SuccessModal";
import "./OnboardContract.css";

const initialContractState = {
  contractTitle: "",
  contractDescription: "",
  contractStartDate: "",
  contractSunsetDate: "",
  ppaTargetStartDate: "",
  firstInvoiceDate: "",
  generator: "",
  offtaker: "",
  projectName: "",
  projectLocation: "",
  projectStartDate: "",
  projectFinishDate: "",
  technologyType: "",
  maximumGeneration: "",
  pricingStructure: "",
  customEscalation: "",
  escalationFrequency: "",
  baseCPIYear: "",
  ceilingPrice: "",
  ppaBundlePrice: "",
  strikePrice: "",
  floatingAmount: "",
  fixedAmount: "",
  location: "Australia",
  regionId: "",
  assetId: "",
  negativePricing: "",
  settlementAndDelivery: "",
  currency: "AUD",
  paymentFrequency: "",
  performanceGuarantees: "",
  LGC: "",
  volume: "",
  environmentalAttributeHandling: "",
  calculateShortfallOrSurplusCompensation: "",
  shortFallThreshold: "",
  surplusThreshold: "",
  numberOfConsecutivePeriodsForShortfall: "",
  numberOfConsecutivePeriodsForSurplus: "",
  metering: "",
  electricityContractVolume: "",
  buyersPercentage: "",
  terminationPayment: "",
  projectOps: "",
  deployInBlockchain: "",
};

const initialFormValidity = {
  contractTitle: "",
  contractDescription: "",
  contractStartDate: "",
  contractSunsetDate: "",
  ppaTargetStartDate: "",
  firstInvoiceDate: "",
  generator: "",
  offtaker: "",
  projectName: "",
  projectLocation: "",
  projectStartDate: "",
  projectFinishDate: "",
  technologyType: "",
  maximumGeneration: "",
  pricingStructure: "",
  customEscalation: "",
  escalationFrequency: "",
  baseCPIYear: "",
  ceilingPrice: "",
  ppaBundlePrice: "",
  strikePrice: "",
  floatingAmount: "",
  fixedAmount: "",
  location: "",
  regionId: "",
  assetId: "",
  negativePricing: "",
  settlementAndDelivery: "",
  currency: "",
  paymentFrequency: "",
  performanceGuarantees: "",
  LGC: "",
  volume: "",
  environmentalAttributeHandling: "",
  calculateShortfallOrSurplusCompensation: "",
  shortFallThreshold: "",
  surplusThreshold: "",
  numberOfConsecutivePeriodsForShortfall: "",
  numberOfConsecutivePeriodsForSurplus: "",
  metering: "",
  electricityContractVolume: "",
  buyersPercentage: "",
  contractFile: null,
  terminationPayment: "",
  projectOps: "",
  deployInBlockchain: "",
  isFormValid: false,
};

const formReducer = (state, action) => {
  if (action.type === "RESET_FORM") {
    return initialContractState;
  } else {
    const { name, value } = action.type;
    const newState = {
      ...state,
      [name]:
        name === "regionId" || name === "assetId"
          ? value?.toUpperCase()
          : value,
    };

    if (name === "pricingStructure") {
      // Clear escalationFrequency when switching to fixed_pricing
      if (value === "fixed_pricing") {
        newState.escalationFrequency = "";
      }

      // Clear customEscalation when switching away from custom
      if (value !== "custom") {
        newState.customEscalation = "";
      }

      // Clear baseCPIYear when switching away from indexed_with_cpi
      if (value !== "indexed_with_cpi") {
        newState.baseCPIYear = "";
      }
    } else if (name === "calculateShortfallOrSurplusCompensation") {
      if (value === "no") {
        newState.shortFallThreshold = "";
        newState.surplusThreshold = "";
        newState.numberOfConsecutivePeriodsForShortfall = "";
        newState.numberOfConsecutivePeriodsForSurplus = "";
      }
    }

    return newState;
  }
};

const requiredFields = [
  "contractTitle",
  "contractStartDate",
  "contractSunsetDate",
  "contractDescription",
  "ppaTargetStartDate",
  "generator",
  "offtaker",
  "technologyType",
  "maximumGeneration",
  "pricingStructure",
  "strikePrice",
  "fixedAmount",
  "floatingAmount",
  "negativePricing",
  "location",
  "currency",
  "paymentFrequency",
  "settlementAndDelivery",
  "LGC",
  "volume",
  "ceilingPrice",
  "electricityContractVolume",
  "buyersPercentage",
  "terminationPayment",
  "projectOps",
  "regionId",
  "assetId",
  "ppaBundlePrice",
  "deployInBlockchain",
  "firstInvoiceDate",
];

const selectFields = [
  "offtaker",
  "generator",
  "location",
  "technologyType",
  "pricingStructure",
  "escalationFrequency",
  "paymentFrequency",
  "regionId",
  "assetId",
  "calculateShortfallOrSurplusCompensation",
  "deployInBlockchain",
];

const countryRegionList = [
  {
    region: "Australia",
    regionIds: ["NSW1", "QLD1", "SA1", "TAS1", "NT1", "WA1", "VIC1"],
  },
];

const isValidDate = (category, data) => {
  let dateValidity = true;
  let dateError = "";
  let startDate,
    sunsetDate,
    ppaDate,
    projectStartDate,
    projectFinishDate,
    firstInvoiceDate;
  if (category === "contractStartDate") {
    if (data.contractStartDate) {
      startDate = new Date(data.contractStartDate).getTime();
      if (data.projectStartDate) {
        projectStartDate = new Date(data.projectStartDate).getTime();
        if (startDate <= projectStartDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Start Date";
        }
      }
      if (data.projectFinishDate) {
        projectFinishDate = new Date(data.projectFinishDate).getTime();
        if (startDate <= projectFinishDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Finish Date";
        }
      }
      if (data.ppaTargetStartDate) {
        ppaDate = new Date(data.ppaTargetStartDate).getTime();
        if (startDate > ppaDate) {
          dateValidity = false;
          dateError = "Date should be less than PPA Start Date";
        }
      }
      if (data.contractSunsetDate) {
        sunsetDate = new Date(data.contractSunsetDate).getTime();
        if (startDate >= sunsetDate) {
          dateValidity = false;
          dateError = "Date should be less than Sunset Date";
        }
      }
      if (data.firstInvoiceDate) {
        firstInvoiceDate = new Date(data.firstInvoiceDate).getTime();
        if (startDate >= firstInvoiceDate) {
          dateValidity = false;
          dateError = "Date should be less than First Invoice Date";
        }
      }
    } else {
      dateValidity = false;
      dateError = "Select Contract Start Date";
    }
  } else if (category === "contractSunsetDate") {
    if (data.contractSunsetDate) {
      sunsetDate = new Date(data.contractSunsetDate).getTime();
      if (data.projectStartDate) {
        projectStartDate = new Date(data.projectStartDate).getTime();
        if (sunsetDate <= projectStartDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Start Date";
        }
      }
      if (data.projectFinishDate) {
        projectFinishDate = new Date(data.projectFinishDate).getTime();
        if (sunsetDate <= projectFinishDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Finish Date";
        }
      }
      if (data.ppaTargetStartDate) {
        const targetDate = new Date(data.ppaTargetStartDate);
        ppaDate = new Date(
          targetDate.getFullYear(),
          targetDate.getMonth() + 1,
          targetDate.getDate(),
        );
        if (sunsetDate <= ppaDate) {
          dateValidity = false;
          dateError =
            "Date should be at least 1 month greater than PPA Start Date";
        }
      }
      if (data.contractStartDate) {
        startDate = new Date(data.contractStartDate).getTime();
        if (sunsetDate <= startDate) {
          dateValidity = false;
          dateError = "Date should be greater than Start Date";
        }
      }
      if (data.firstInvoiceDate) {
        firstInvoiceDate = new Date(data.firstInvoiceDate).getTime();
        if (sunsetDate <= firstInvoiceDate) {
          dateValidity = false;
          dateError = "Date should be greater than First Invoice Date";
        }
      }
    } else {
      dateValidity = false;
      dateError = "Select Contract Sunset Date";
    }
  } else if (category === "ppaTargetStartDate") {
    if (data.ppaTargetStartDate) {
      ppaDate = new Date(data.ppaTargetStartDate).getTime();
      if (data.projectStartDate) {
        projectStartDate = new Date(data.projectStartDate).getTime();
        if (ppaDate <= projectStartDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Start Date";
        }
      }
      if (data.projectFinishDate) {
        projectFinishDate = new Date(data.projectFinishDate).getTime();
        if (ppaDate <= projectFinishDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Finish Date";
        }
      }
      if (data.contractSunsetDate) {
        sunsetDate = new Date(data.contractSunsetDate).getTime();
        if (ppaDate >= sunsetDate) {
          dateValidity = false;
          dateError = "Date should be less than Sunset Date";
        }
      }
      if (data.contractStartDate) {
        startDate = new Date(data.contractStartDate).getTime();
        if (ppaDate < startDate) {
          dateValidity = false;
          dateError = "Date should be greater than Start Date";
        }
      }
      if (data.firstInvoiceDate) {
        firstInvoiceDate = new Date(data.firstInvoiceDate).getTime();
        if (ppaDate >= firstInvoiceDate) {
          dateValidity = false;
          dateError = "Date should be less than First Invoice Date";
        }
      }
    } else {
      dateValidity = false;
      dateError = "Select PPA Target Start Date";
    }
  } else if (category === "projectStartDate") {
    if (data.projectStartDate) {
      projectStartDate = new Date(data.projectStartDate).getTime();
      if (data.projectFinishDate) {
        projectFinishDate = new Date(data.projectFinishDate).getTime();
        if (projectStartDate >= projectFinishDate) {
          dateValidity = false;
          dateError = "Date should be less than Project Finish Date";
        }
      }
      if (data.ppaTargetStartDate) {
        ppaDate = new Date(data.ppaTargetStartDate).getTime();
        if (projectStartDate >= ppaDate) {
          dateValidity = false;
          dateError = "Date should be less than PPA Start Date";
        }
      }
      if (data.contractSunsetDate) {
        sunsetDate = new Date(data.contractSunsetDate).getTime();
        if (projectStartDate >= sunsetDate) {
          dateValidity = false;
          dateError = "Date should be less than Sunset Date";
        }
      }
      if (data.contractStartDate) {
        startDate = new Date(data.contractStartDate).getTime();
        if (projectStartDate >= startDate) {
          dateValidity = false;
          dateError = "Date should be less than Contract Start Date";
        }
      }
      if (data.firstInvoiceDate) {
        firstInvoiceDate = new Date(data.firstInvoiceDate).getTime();
        if (projectStartDate >= firstInvoiceDate) {
          dateValidity = false;
          dateError = "Date should be less than First Invoice Date";
        }
      }
    }
  } else if (category === "projectFinishDate") {
    if (data.projectFinishDate) {
      projectFinishDate = new Date(data.projectFinishDate).getTime();
      if (data.projectStartDate) {
        projectStartDate = new Date(data.projectStartDate).getTime();
        if (projectFinishDate <= projectStartDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Start Date";
        }
      }
      if (data.ppaTargetStartDate) {
        ppaDate = new Date(data.ppaTargetStartDate).getTime();
        if (projectFinishDate >= ppaDate) {
          dateValidity = false;
          dateError = "Date should be less than PPA Start Date";
        }
      }
      if (data.contractSunsetDate) {
        sunsetDate = new Date(data.contractSunsetDate).getTime();
        if (projectFinishDate >= sunsetDate) {
          dateValidity = false;
          dateError = "Date should be less than Sunset Date";
        }
      }
      if (data.contractStartDate) {
        startDate = new Date(data.contractStartDate).getTime();
        if (projectFinishDate >= startDate) {
          dateValidity = false;
          dateError = "Date should be less than Contract Start Date";
        }
      }
      if (data.firstInvoiceDate) {
        firstInvoiceDate = new Date(data.firstInvoiceDate).getTime();
        if (projectFinishDate >= firstInvoiceDate) {
          dateValidity = false;
          dateError = "Date should be less than First Invoice Date";
        }
      }
    }
  } else {
    if (data.firstInvoiceDate) {
      firstInvoiceDate = new Date(data.firstInvoiceDate).getTime();
      if (data.projectStartDate) {
        projectStartDate = new Date(data.projectStartDate).getTime();
        if (firstInvoiceDate <= projectStartDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Start Date";
        }
      }
      if (data.projectFinishDate) {
        projectFinishDate = new Date(data.projectFinishDate).getTime();
        if (firstInvoiceDate <= projectFinishDate) {
          dateValidity = false;
          dateError = "Date should be greater than Project Finish Date";
        }
      }
      if (data.ppaTargetStartDate) {
        ppaDate = new Date(data.ppaTargetStartDate).getTime();
        if (firstInvoiceDate <= ppaDate) {
          dateValidity = false;
          dateError = "Date should be greater than PPA Start Date";
        }
      }
      if (data.contractSunsetDate) {
        sunsetDate = new Date(data.contractSunsetDate).getTime();
        if (firstInvoiceDate >= sunsetDate) {
          dateValidity = false;
          dateError = "Date should be less than Sunset Date";
        }
      }
      if (data.contractStartDate) {
        startDate = new Date(data.contractStartDate).getTime();
        if (firstInvoiceDate <= startDate) {
          dateValidity = false;
          dateError = "Date should be greater than Contract Start Date";
        }
      }
    }
  }
  return { isValid: dateValidity, error: dateError };
};

const formValidityReducer = (state, action) => {
  let isValid = false;
  if (action.type === "RESET_FORM_VALIDITY") {
    return action.payload;
  } else {
    let errorData = { ...state };
    switch (action.type) {
      case "CONTRACTFILESUCCESS": {
        errorData = { ...errorData, contractFile: "" };
        break;
      }
      case "CONTRACTFILEFAILED": {
        errorData = {
          ...errorData,
          contractFile: "Error: Upload the contract file",
        };
        break;
      }
      case "CONTRACTSTARTDATE":
      case "CONTRACTSUNSETDATE":
      case "PPATARGETSTARTDATE":
      case "FIRSTINVOICEDATE":
      case "PROJECTSTARTDATE":
      case "PROJECTFINISHDATE": {
        const requiredDate = [
          "contractStartDate",
          "contractSunsetDate",
          "ppaTargetStartDate",
          "projectStartDate",
          "projectFinishDate",
          "firstInvoiceDate",
        ];
        var errObj = {};
        requiredDate.forEach((item) => {
          const data = action.payload[item]
            ? isValidDate(item, action.payload)
            : ["projectStartDate", "projectFinishDate"].includes(item)
              ? { isValid: true }
              : {};
          errObj[item] = data.isValid ? "" : data.error;
        });
        errorData = { ...errorData, ...errObj };
        break;
      }
      case "CONTRACTTITLE": {
        isValid =
          action.payload.contractTitle.length &&
          whiteSpaceStringRegex.test(action.payload.contractTitle) > 0
            ? true
            : false;
        errorData = {
          ...errorData,
          contractTitle: isValid ? "" : "Enter Contract Title",
        };
        break;
      }
      case "CONTRACTDESCRIPTION": {
        isValid =
          action.payload.contractDescription.length > 0 &&
          whiteSpaceStringRegex.test(action.payload.contractDescription)
            ? true
            : false;
        errorData = {
          ...errorData,
          contractDescription: isValid ? "" : "Enter Contract Description",
        };
        break;
      }
      case "GENERATOR": {
        isValid = action.payload.generator.length > 0 ? true : false;
        errorData = {
          ...errorData,
          generator: isValid ? "" : "Select Generator",
        };
        break;
      }
      case "OFFTAKER": {
        isValid = action.payload.offtaker.length > 0 ? true : false;
        errorData = {
          ...errorData,
          offtaker: isValid ? "" : "Select Offtaker",
        };
        break;
      }
      case "TECHNOLOGYTYPE": {
        isValid = action.payload.technologyType.length > 0 ? true : false;
        errorData = {
          ...errorData,
          technologyType: isValid ? "" : "Select Technology Type",
        };
        break;
      }
      case "MAXIMUMGENERATION": {
        isValid = decimalNumberRegex.test(action.payload.maximumGeneration)
          ? true
          : false;
        errorData = {
          ...errorData,
          maximumGeneration: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "PRICINGSTRUCTURE": {
        isValid = action.payload.pricingStructure.length > 0 ? true : false;

        // Also validate escalationFrequency when pricing structure changes
        const requiresFrequency =
          action.payload.pricingStructure === "indexed_with_cpi" ||
          action.payload.pricingStructure === "custom";
        const escalationFrequencyValid =
          !requiresFrequency ||
          (requiresFrequency && action.payload.escalationFrequency.length > 0);

        // Also validate customEscalation when pricing structure changes
        const requiresCustom = action.payload.pricingStructure === "custom";
        const customEscalationValid =
          !requiresCustom ||
          (requiresCustom &&
            decimalNumberRegex.test(action.payload.customEscalation) &&
            action.payload.customEscalation > 0);

        // Also validate baseCPIYear when pricing structure changes
        const requiresBaseCPIYear =
          action.payload.pricingStructure === "indexed_with_cpi";
        const baseCPIYearValid =
          !requiresBaseCPIYear ||
          (requiresBaseCPIYear && dateRegex.test(action.payload.baseCPIYear));

        errorData = {
          ...errorData,
          pricingStructure: isValid ? "" : "Select Pricing Structure",
          escalationFrequency: escalationFrequencyValid
            ? ""
            : "Select Escalation Frequency",
          customEscalation: customEscalationValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
          baseCPIYear: baseCPIYearValid ? "" : "Select Base CPI Year",
        };
        break;
      }
      case "CUSTOMESCALATION": {
        isValid =
          action.payload.pricingStructure !== "custom"
            ? true
            : decimalNumberRegex.test(action.payload.customEscalation) &&
              action.payload.customEscalation > 0;
        errorData = {
          ...errorData,
          customEscalation: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "ESCALATIONFREQUENCY": {
        const requiresFrequency =
          action.payload.pricingStructure === "indexed_with_cpi" ||
          action.payload.pricingStructure === "custom";
        isValid =
          !requiresFrequency ||
          (requiresFrequency && action.payload.escalationFrequency.length > 0);
        errorData = {
          ...errorData,
          escalationFrequency: isValid ? "" : "Select Escalation Frequency",
        };
        break;
      }

      case "BASECPIYEAR": {
        const requiresBaseCPIYear =
          action.payload.pricingStructure === "indexed_with_cpi";
        // If base cpi year is required, check whether a valid date in YYYY-MM-DD format is entered. If not required, consider it valid even if it's empty
        isValid =
          !requiresBaseCPIYear ||
          (requiresBaseCPIYear && dateRegex.test(action.payload.baseCPIYear));
        errorData = {
          ...errorData,
          baseCPIYear: isValid ? "" : "Select Base CPI Year",
        };
        break;
      }
      case "CEILINGPRICE": {
        isValid = decimalNumberRegex.test(action.payload.ceilingPrice)
          ? true
          : false;
        errorData = {
          ...errorData,
          ceilingPrice: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "STRIKEPRICE": {
        isValid =
          decimalNumberRegex.test(action.payload.strikePrice) &&
          action.payload.strikePrice > 0
            ? true
            : false;
        var errorObj = {
          strikePrice: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        if (
          parseFloat(action.payload.ppaBundlePrice) > 0 &&
          parseFloat(action.payload.strikePrice) >
            parseFloat(action.payload.ppaBundlePrice)
        )
          errorObj.strikePrice =
            "Strike Price should be less than Bundle Price";
        else errorObj.ppaBundlePrice = "";
        errorData = { ...errorData, ...errorObj };
        break;
      }
      case "PPABUNDLEPRICE": {
        isValid = decimalNumberRegex.test(action.payload.ppaBundlePrice)
          ? true
          : false;
        var errorObj = {
          ppaBundlePrice: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        if (
          parseFloat(action.payload.ppaBundlePrice) > 0 &&
          parseFloat(action.payload.strikePrice) >
            parseFloat(action.payload.ppaBundlePrice)
        )
          errorObj.ppaBundlePrice =
            "Bundle Price should be greater than Strike Price";
        else errorObj.strikePrice = "";

        errorData = { ...errorData, ...errorObj };
        break;
      }
      case "FIXEDAMOUNT": {
        isValid = decimalNumberRegex.test(action.payload.fixedAmount)
          ? true
          : false;
        errorData = {
          ...errorData,
          fixedAmount: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "FLOATINGAMOUNT": {
        isValid = decimalNumberRegex.test(action.payload.floatingAmount)
          ? true
          : false;
        errorData = {
          ...errorData,
          floatingAmount: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "NEGATIVEPRICING": {
        isValid =
          negativeNumberRegex.test(action.payload.negativePricing) &&
          action.payload.negativePricing <= 0
            ? true
            : false;
        errorData = {
          ...errorData,
          negativePricing: isValid
            ? ""
            : "Enter Valid Negative Number Upto 2 Decimal Places",
        };
        break;
      }
      case "LOCATION": {
        isValid = action.payload.location.length > 0 ? true : false;
        errorData = { ...errorData, location: isValid ? "" : "Enter Location" };
        break;
      }
      case "ASSETID":
        isValid =
          action.payload.assetId.length > 0 &&
          whiteSpaceStringRegex.test(action.payload.assetId)
            ? true
            : false;
        errorData = {
          ...errorData,
          assetId: isValid ? "" : "Provide Asset Id",
        };
        break;
      case "REGIONID": {
        isValid =
          action.payload.regionId.length > 0 &&
          whiteSpaceStringRegex.test(action.payload.regionId)
            ? true
            : false;
        errorData = {
          ...errorData,
          regionId: isValid ? "" : "Provide Region Id",
        };
        break;
      }
      case "CURRENCY": {
        isValid = action.payload.currency.length > 0 ? true : false;
        errorData = { ...errorData, currency: isValid ? "" : "Enter Currency" };
        break;
      }
      case "PAYMENTFREQUENCY": {
        isValid = action.payload.paymentFrequency.length > 0 ? true : false;
        errorData = {
          ...errorData,
          paymentFrequency: isValid ? "" : "Select Payment Frequency",
        };
        break;
      }
      case "SETTLEMENTANDDELIVERY": {
        isValid =
          action.payload.settlementAndDelivery.length > 0 &&
          whiteSpaceStringRegex.test(action.payload.settlementAndDelivery)
            ? true
            : false;
        errorData = {
          ...errorData,
          settlementAndDelivery: isValid ? "" : "Enter Settlement And Delivery",
        };
        break;
      }
      case "PERFORMANCEGUARANTEES": {
        isValid = decimalNumberRegex.test(action.payload.performanceGuarantees)
          ? true
          : false;
        errorData = {
          ...errorData,
          performanceGuarantees: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "LGC": {
        isValid = decimalNumberRegex.test(action.payload.LGC) ? true : false;
        errorData = {
          ...errorData,
          LGC: isValid ? "" : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "VOLUME": {
        isValid = decimalNumberRegex.test(action.payload.volume) ? true : false;
        errorData = {
          ...errorData,
          volume: isValid ? "" : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "CALCULATESHORTFALLORSURPLUSCOMPENSATION": {
        isValid =
          action.payload.calculateShortfallOrSurplusCompensation.length > 0
            ? true
            : false;
        errorData = {
          ...errorData,
          calculateShortfallOrSurplusCompensation: isValid
            ? ""
            : "Select Base CPI Year",
        };
        break;
      }
      case "SHORTFALLTHRESHOLD": {
        const requiresShortFallThreshold =
          action.payload.calculateShortfallOrSurplusCompensation === "yes";

        isValid =
          !requiresShortFallThreshold ||
          (requiresShortFallThreshold &&
            decimalNumberRegex.test(action.payload.shortFallThreshold))
            ? true
            : false;
        errorData = {
          ...errorData,
          shortFallThreshold: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "SURPLUSTHRESHOLD": {
        const requiresSurplusThreshold =
          action.payload.calculateShortfallOrSurplusCompensation === "yes";

        isValid =
          !requiresSurplusThreshold ||
          (requiresSurplusThreshold &&
            decimalNumberRegex.test(action.payload.surplusThreshold))
            ? true
            : false;
        errorData = {
          ...errorData,
          surplusThreshold: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "NUMBEROFCONSECUTIVEPERIODSFORSHORTFALL": {
        const requiresNumberOfConsecutivePeriodsForShortfall =
          action.payload.calculateShortfallOrSurplusCompensation === "yes";

        isValid =
          !requiresNumberOfConsecutivePeriodsForShortfall ||
          (requiresNumberOfConsecutivePeriodsForShortfall &&
            wholeNumberRegex.test(
              action.payload.numberOfConsecutivePeriodsForShortfall,
            ))
            ? true
            : false;
        errorData = {
          ...errorData,
          numberOfConsecutivePeriodsForShortfall: isValid
            ? ""
            : "Enter Valid Number",
        };
        break;
      }
      case "NUMBEROFCONSECUTIVEPERIODSFORSURPLUS": {
        const requiresNumberOfConsecutivePeriodsForSurplus =
          action.payload.calculateShortfallOrSurplusCompensation === "yes";

        isValid =
          !requiresNumberOfConsecutivePeriodsForSurplus ||
          (requiresNumberOfConsecutivePeriodsForSurplus &&
            wholeNumberRegex.test(
              action.payload.numberOfConsecutivePeriodsForSurplus,
            ))
            ? true
            : false;
        errorData = {
          ...errorData,
          numberOfConsecutivePeriodsForSurplus: isValid
            ? ""
            : "Enter Valid Number",
        };
        break;
      }
      case "ENIVIRONMENTALATTRIBUTEHANDLING": {
        isValid =
          action.payload.environmentalAttributeHandling.length > 0
            ? true
            : false;
        errorData = {
          ...errorData,
          environmentalAttributeHandling: isValid
            ? ""
            : "Select Environmental Attribute Handling",
        };
        break;
      }
      case "ELECTRICITYCONTRACTVOLUME": {
        isValid = decimalNumberRegex.test(
          action.payload.electricityContractVolume,
        )
          ? true
          : false;
        errorData = {
          ...errorData,
          electricityContractVolume: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "BUYERSPERCENTAGE": {
        isValid = decimalNumberRegex.test(action.payload.buyersPercentage)
          ? true
          : false;
        errorData = {
          ...errorData,
          buyersPercentage: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "TERMINATIONPAYMENT": {
        isValid = decimalNumberRegex.test(action.payload.terminationPayment)
          ? true
          : false;
        errorData = {
          ...errorData,
          terminationPayment: isValid
            ? ""
            : "Enter Valid Number Upto 2 Decimal Places",
        };
        break;
      }
      case "PROJECTOPS": {
        isValid =
          action.payload.projectOps.length > 0 &&
          whiteSpaceStringRegex.test(action.payload.projectOps)
            ? true
            : false;
        errorData = {
          ...errorData,
          projectOps: isValid ? "" : "Enter Project Ops",
        };
        break;
      }
      case "METERING": {
        isValid =
          action.payload.metering.length === 0 ||
          (action.payload.metering.length > 0 &&
            whiteSpaceStringRegex.test(action.payload.metering))
            ? true
            : false;
        errorData = { ...errorData, metering: isValid ? "" : "Enter Metering" };
        break;
      }
      case "DEPLOYINBLOCKCHAIN": {
        isValid = action.payload.deployInBlockchain.length > 0 ? true : false;
        errorData = {
          ...errorData,
          deployInBlockchain: isValid ? "" : "Select Any Of The Value",
        };
        break;
      }
      default:
        console.log("Nothing matched");
    }
    const { isFormValid, ...errors } = errorData;
    if (
      requiredFields.some((key) => action.payload[key] === "") ||
      Object.keys(errors).some((key) => errors[key] !== "")
    ) {
      errorData.isFormValid = false;
    } else {
      errorData.isFormValid = true;
    }

    return errorData;
  }
};

const technologies = [
  { name: "Solar", value: "solar" },
  { name: "Wind", value: "wind" },
  { name: "Hydro", value: "hydro" },
];

const pricing = [
  { name: "Fixed Pricing", value: "fixed_pricing" },
  { name: "Indexed With CPI", value: "indexed_with_cpi" },
  { name: "Custom", value: "custom" },
];

const escalationFrequencies = [
  { name: "Monthly", value: "monthly" },
  { name: "Quarterly", value: "quarterly" },
  { name: "Annually", value: "annually" },
  { name: "Financial Year", value: "financial_year" },
];

const paymentFrequencies = [
  { name: "Monthly", value: "monthly" },
  { name: "Quarterly", value: "quarterly" },
  { name: "Annually", value: "annually" },
];

const attributes = [
  { name: "RECs", value: "RECs" },
  { name: "LGC", value: "LGC" },
  { name: "Carbon Credits", value: "carbon_credits" },
];

export default function AdminContractOnboarding({ title }) {
  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [generators, setGenerators] = useState([]);
  const [offtakers, setOfftakers] = useState([]);
  const [regionIdList, setRegionIdList] = useState([]);
  const [contractParams, setContractParams] = useReducer(
    formReducer,
    initialContractState,
  );
  const [contractFormValidity, setContractFormValidity] = useReducer(
    formValidityReducer,
    initialFormValidity,
  );
  const { setLoading } = useContext(LoaderContext);
  const { ErrorToast } = useContext(MessageContext);
  const fileRef = useRef(null);
  const payloadValidator = [
    { key: "contractTitle", label: "Contract Title" },
    { key: "contractDescription", label: "Contract Description" },
    { key: "contractStartDate", label: "Contract Start Date" },
    { key: "contractSunsetDate", label: "Contract Sunset Date" },
    { key: "ppaTargetStartDate", label: "PPA Target Start Date" },
    { key: "firstInvoiceDate", label: "First Invoice Date" },
    { key: "generator", label: "Generator" },
    { key: "offtaker", label: "Offtaker" },
    { key: "projectName", label: "Project Name" },
    { key: "projectLocation", label: "Project Location" },
    { key: "projectStartDate", label: "Project Start Date" },
    { key: "projectFinishDate", label: "Project Finish Date" },
    { key: "technologyType", label: "Technology Type" },
    { key: "maximumGeneration", label: "Maximum Generation (MWh)" },
    { key: "pricingStructure", label: "Pricing Structure" },
    { key: "escalationFrequency", label: "Escalation Frequency" },
    { key: "baseCPIYear", label: "Base CPI Year" },
    { key: "customEscalation", label: "Custom Escalation (%)" },
    { key: "ceilingPrice", label: "Ceiling Price" },
    { key: "ppaBundlePrice", label: "PPA Bundle Price" },
    { key: "strikePrice", label: "Strike Price" },
    { key: "floatingAmount", label: "Floating Amount" },
    { key: "fixedAmount", label: "Fixed Amount" },
    { key: "location", label: "Location" },
    { key: "assetId", label: "Asset Id" },
    { key: "regionId", label: "Region Id" },
    { key: "negativePricing", label: "Negative Pricing" },
    { key: "settlementAndDelivery", label: "Settlement And Delivery" },
    { key: "currency", label: "Currency" },
    { key: "paymentFrequency", label: "Payment Frequency" },
    { key: "performanceGuarantees", label: "Performance Guarantees" },
    { key: "LGC", label: "LGC/REC (Volume)" },
    { key: "volume", label: "Volume (MWh)" },
    {
      key: "environmentalAttributeHandling",
      label: "Environmental Attribute Handling",
    },
    {
      key: "calculateShortfallOrSurplusCompensation",
      label: "Calculate Shortfall/Surplus Compensation",
    },
    { key: "shortFallThreshold", label: "Shortfall Threshold" },
    { key: "surplusThreshold", label: "Surplus Threshold" },
    { key: "metering", label: "Metering" },
    {
      key: "electricityContractVolume",
      label: "Electricity Contract Volume (MWh)",
    },
    { key: "buyersPercentage", label: "Buyers Percentage" },
    { key: "contractFile", label: "Contract File" },
    { key: "terminationPayment", label: "Termination Payment" },
    { key: "projectOps", label: "Project Operation" },
    {
      key: "numberOfConsecutivePeriodsForShortfall",
      label: "Number Of Consecutive Periods For Shortfall",
    },
    {
      key: "numberOfConsecutivePeriodsForSurplus",
      label: "Number Of Consecutive Periods For Surplus",
    },
    { key: "deployInBlockchain", label: "Deploy In Blockchain" },
  ];

  const { location, generator } = contractParams;

  useEffect(() => {
    const list = countryRegionList.find((item) => {
      return item.region === location;
    });
    setRegionIdList(list ? list.regionIds : []);
    setContractParams({ type: { name: "regionId", value: "" } });
    setContractFormValidity({
      type: "LOCATION",
      payload: { ...contractParams, regionId: "" },
    });
  }, [location]);

  useEffect(() => {
    setContractParams({ type: { name: "assetId", value: "" } });
    setContractFormValidity({
      type: "ASSETID",
      payload: { ...contractParams, assetId: "" },
    });
  }, [generator]);

  const removeFile = () => {
    setSelectedContract(null);
    fileRef.current.value = "";
    setContractFormValidity({
      type: "CONTRACTFILEFAILED",
      payload: contractParams,
    });
  };

  const getClients = async () => {
    try {
      setLoading(true);
      const [generators, offtakers] = await Promise.all([
        axios.get(`${config.BASE_URL}/api/client?type=generator`),
        axios.get(`${config.BASE_URL}/api/client?type=offtaker`),
      ]);
      setGenerators(generators.data);
      setOfftakers(offtakers.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        ErrorToast(error.response.data.message);
      } else {
        ErrorToast("Something Went Wrong!. Please Try Again");
      }
    }
  };

  useEffect(() => {
    getClients();
  }, []);

  const handleSubmit = () => {
    window.scrollTo(0, 0);
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedContract(null);
    setContractParams({ type: "RESET_FORM" });
    setContractFormValidity({
      type: "RESET_FORM_VALIDITY",
      payload: initialFormValidity,
    });
    fileRef.current.value = "";
  };

  const submitContractOnboarding = async () => {
    try {
      setLoading(true);
      let formData = new FormData();
      formData.append("contractFile", selectedContract);
      const payload = { ...contractParams };
      Object.keys(payload).forEach((item) => {
        if (requiredFields.includes(item)) {
          formData.append(item, payload[item]);
        } else if (payload[item]) {
          formData.append(item, payload[item]);
        }
      });
      await axios.post(`${config.BASE_URL}/api/contract`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setSelectedContract(null);
      setShowSuccess(true);
      setShowModal(false);
      setContractParams({ type: "RESET_FORM" });
      setContractFormValidity({
        type: "RESET_FORM_VALIDITY",
        payload: initialFormValidity,
      });
      setLoading(false);
      fileRef.current.value = "";
    } catch (error) {
      setLoading(false);
      setShowModal(false);
      if (error?.response?.data?.errors?.[0]?.property) {
        ErrorToast(
          `Invalid value provided for ${error.response.data.errors[0].property}`,
        );
      } else if (error?.response?.data?.message) {
        ErrorToast(error.response.data.message);
      } else {
        ErrorToast("Something Went Wrong!. Please Try Again");
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedContract(file);
      setContractFormValidity({
        type: "CONTRACTFILESUCCESS",
        payload: contractParams,
      });
    } else {
      if (selectedContract) {
        let list = new DataTransfer();
        let file = new File([selectedContract], selectedContract.name);
        list.items.add(file);
        let fileList = list.files;
        fileRef.current.files = fileList;
      } else {
        setContractFormValidity({
          type: "CONTRACTFILEFAILED",
          payload: contractParams,
        });
      }
    }
  };

  const handleCalValue = (value, category) => {
    setContractParams({ type: { name: category, value } });
    setContractFormValidity({
      type: category.toUpperCase(),
      payload: { ...contractParams, [category]: value },
    });
  };

  const handleChange = (e) => {
    if (selectFields.includes(e.target.name) && e.target?.type !== "text") {
      setContractParams({ type: e.target });
      const { name, value } = e.target;
      setContractFormValidity({
        type: name.toUpperCase(),
        payload: { ...contractParams, [name]: value },
      });
    } else {
      setContractParams({ type: e.target });
    }
  };

  const handleValidation = (e) => {
    setContractFormValidity({
      type: e.target.name.toUpperCase(),
      payload: contractParams,
    });
  };

  const assets =
    generators?.find((gen) => gen.id === contractParams?.generator)?.assets ||
    [];

  const renderColumnElements = (items) => {
    return items.map((item) => {
      if (item.type === "text") {
        return (
          <div className="admin__contract__form__group" key={item.key}>
            <label htmlFor={item.key}>
              {item.label} {item.required && "*"}
            </label>
            <input
              onChange={handleChange}
              onKeyUp={handleValidation}
              value={contractParams[item.key]}
              className="admin__contract__input"
              type={item.type}
              id={item.key}
              name={item.key}
              placeholder={item.placeholder}
              required={item.required}
            />
            {contractFormValidity[item.key] && (
              <p className="text_error">{contractFormValidity[item.key]}</p>
            )}
          </div>
        );
      } else if (item.type === "date") {
        return (
          <DatePicker
            key={item.key}
            minDate={item.minDate ? item.minDate : null}
            error={contractFormValidity[item.key]}
            formClass={"admin__contract__form__group"}
            label={`${item.label} ${item.required && "*"}`}
            CalValue={contractParams[item.key]}
            setCalValue={handleCalValue}
            id={item.key}
            placeholder={item.placeholder}
            category={item.key}
          />
        );
      } else if (item.type === "select") {
        return (
          <div className="admin__contract__form__group" key={item.key}>
            <label htmlFor={item.key}>
              {item.label} {item.required && "*"}
            </label>
            <select
              className="admin__contract__input"
              id={item.key}
              name={item.key}
              value={contractParams[item.key]}
              onChange={handleChange}
              required={item.required}
            >
              <option value="">{item.defaultOption}</option>
              {item.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.name}
                </option>
              ))}
            </select>
            {contractFormValidity[item.key] && (
              <p className="text_error">{contractFormValidity[item.key]}</p>
            )}
          </div>
        );
      } else if (item.type === "file") {
        return (
          <div className="admin__contract__form__group" key={item.key}>
            <label htmlFor={item.key}>
              {item.label} {item.required && "*"}
            </label>
            <div className="close_icon">
              {selectedContract ? (
                <img
                  className="cross__icon"
                  onClick={removeFile}
                  src={"/icons/cross_black.svg"}
                  alt="clear_cross"
                />
              ) : null}
            </div>
            <input
              ref={item.ref}
              accept={item.accept}
              onChange={(e) => handleFileChange(e)}
              className="admin__contract__input__file"
              type={item.type}
              id={item.key}
              name={item.key}
              placeholder={item.placeholder}
              required={item.required}
            />
            {contractFormValidity.contractFile && (
              <p className="text_error">{contractFormValidity.contractFile}</p>
            )}
          </div>
        );
      } else {
        return null;
      }
    });
  };

  const initialElements = [
    {
      label: "Contract Title",
      key: "contractTitle",
      required: true,
      type: "text",
      placeholder: "Type here",
    },
    {
      label: "Contract Description",
      key: "contractDescription",
      required: true,
      type: "text",
      placeholder: "Type here",
    },
    {
      label: "Contract Start Date",
      key: "contractStartDate",
      required: true,
      type: "date",
      placeholder: "Select Date (Required)",
    },
    {
      label: "Contract Sunset Date",
      key: "contractSunsetDate",
      required: true,
      type: "date",
      placeholder: "Select Date (Required)",
      minDate: new Date(),
    },
    {
      label: "PPA Target Start Date",
      key: "ppaTargetStartDate",
      required: true,
      type: "date",
      placeholder: "Select Date (Required)",
    },
    {
      label: "First Invoice Date",
      key: "firstInvoiceDate",
      required: true,
      type: "date",
      placeholder: "Select Date (Required)",
    },
    {
      label: "Generator",
      key: "generator",
      required: true,
      type: "select",
      defaultOption: "Select Generator",
      options: generators.map((gen) => ({
        name: gen.clientName,
        value: gen.id,
      })),
    },
    {
      label: "Offtaker",
      key: "offtaker",
      required: true,
      type: "select",
      defaultOption: "Select Offtaker",
      options: offtakers.map((off) => ({
        name: off.clientName,
        value: off.id,
      })),
    },
    {
      label: "Project Name",
      key: "projectName",
      required: false,
      type: "text",
      placeholder: "Enter Project Name",
    },
    {
      label: "Project Location",
      key: "projectLocation",
      required: false,
      type: "text",
      placeholder: "Type here",
    },
    {
      label: "Project Start Date",
      key: "projectStartDate",
      required: false,
      type: "date",
      placeholder: "Select Date",
    },
    {
      label: "Project Finish Date",
      key: "projectFinishDate",
      required: false,
      type: "date",
      placeholder: "Select Date",
    },
    {
      label: "Technology Type",
      key: "technologyType",
      required: true,
      type: "select",
      defaultOption: "Select Technology Type",
      options: technologies,
    },
    {
      label: "Maximum Generation (MWh)",
      key: "maximumGeneration",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Pricing Structure",
      key: "pricingStructure",
      required: true,
      type: "select",
      defaultOption: "Select Pricing Structure",
      options: pricing,
    },
  ];

  const conditionalElements =
    contractParams.pricingStructure === "custom"
      ? [
          {
            label: "Custom Escalation (%)",
            key: "customEscalation",
            required: true,
            type: "text",
            placeholder: "0.00",
          },
        ]
      : [];

  if (
    contractParams.pricingStructure === "indexed_with_cpi" ||
    contractParams.pricingStructure === "custom"
  ) {
    conditionalElements.push({
      label: "Escalation Frequency",
      key: "escalationFrequency",
      required: true,
      type: "select",
      defaultOption: "Select Escalation Frequency",
      options: escalationFrequencies,
    });
  }

  if (contractParams.pricingStructure === "indexed_with_cpi") {
    conditionalElements.push({
      label: "Base CPI Year",
      key: "baseCPIYear",
      required: true,
      type: "date",
      placeholder: "Select Base CPI Year",
    });
  }

  const remainingElementsAfterPricingStructure = [
    {
      label: "Ceiling Price",
      key: "ceilingPrice",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "PPA Bundle Price (Green + Black Energy)",
      key: "ppaBundlePrice",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Strike Price",
      key: "strikePrice",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Floating Amount",
      key: "floatingAmount",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Fixed Amount",
      key: "fixedAmount",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Location",
      key: "location",
      required: true,
      type: "select",
      defaultOption: "Select Location",
      options: CountryList.map((country) => ({
        name: country,
        value: country,
      })),
    },
    {
      label: "Asset Id",
      key: "assetId",
      required: true,
      type: "select",
      defaultOption: "Select Asset",
      options: assets.map((asset) => ({
        name: asset,
        value: asset,
      })),
    },
    {
      label: "Region Id",
      key: "regionId",
      required: true,
      type: location === "Australia" ? "select" : "text",
      defaultOption: "Select Region Id",
      options: regionIdList.map((region) => ({
        name: region,
        value: region,
      })),
      placeholder: location !== "Australia" ? "Type here" : "",
    },
    {
      label: "Settlement And Delivery",
      key: "settlementAndDelivery",
      required: true,
      type: "text",
      placeholder: "Type here",
    },
    {
      label: "Currency",
      key: "currency",
      required: true,
      type: "select",
      defaultOption: "Select Currency",
      options: CurrencyList.map((currency) => ({
        name: currency,
        value: currency,
      })),
    },
    {
      label: "Negative Pricing",
      key: "negativePricing",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Performance Guarantees",
      key: "performanceGuarantees",
      required: false,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "LGC/REC (Volume)",
      key: "LGC",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Payment Frequency",
      key: "paymentFrequency",
      required: true,
      type: "select",
      defaultOption: "Select Payment Frequency",
      options: paymentFrequencies,
    },
    {
      label: "Environmental Attribute Handling",
      key: "environmentalAttributeHandling",
      required: false,
      type: "select",
      defaultOption: "Select Environmental Attribute Handling",
      options: attributes,
    },
    {
      label: "Volume (MWh)",
      key: "volume",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Buyers Percentage (%)",
      key: "buyersPercentage",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Metering",
      key: "metering",
      required: false,
      type: "text",
      placeholder: "Type here",
    },
    {
      label: "Electricity Contract Volume (MWh)",
      key: "electricityContractVolume",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Calculate Shortfall/Surplus Compensation",
      key: "calculateShortfallOrSurplusCompensation",
      required: true,
      type: "select",
      defaultOption: "Select Any Of The Value",
      options: [
        { name: "Yes", value: "yes" },
        { name: "No", value: "no" },
      ],
    },
  ];

  const conditionlElementsForShortFallOrSurplus = [
    {
      label: "Shortfall Threshold (MWh)",
      key: "shortFallThreshold",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Surplus Threshold (MWh)",
      key: "surplusThreshold",
      required: true,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Consecutive Periods For Shortfall",
      key: "numberOfConsecutivePeriodsForShortfall",
      required: true,
      type: "text",
      placeholder: "0",
    },
    {
      label: "Consecutive Periods For Surplus",
      key: "numberOfConsecutivePeriodsForSurplus",
      required: true,
      type: "text",
      placeholder: "Type here",
    },
  ];

  const finalElements = [
    {
      label: "Contract Upload",
      key: "contractFile",
      required: true,
      type: "file",
      placeholder: "Contract File",
      ref: fileRef,
      accept: "application/pdf",
    },
    {
      label: "Termination Payment",
      key: "terminationPayment",
      required: false,
      type: "text",
      placeholder: "0.00",
    },
    {
      label: "Project Operation",
      key: "projectOps",
      required: true,
      type: "text",
      placeholder: "Type here",
    },
    {
      label: "Deploy In Blockchain",
      key: "deployInBlockchain",
      required: true,
      type: "select",
      defaultOption: "Select Any Of The Value",
      options: [
        { name: "Yes", value: "yes" },
        { name: "No", value: "no" },
      ],
    },
  ];

  return (
    <>
      <div className="admin__contract__onboarding__wrapper">
        <div className="admin__contract__title">Onboarding Contract Form</div>
        <div className="admin__contract__onboarding__form__container">
          <form
            className="admin__contract__onboarding__form admin__contract__form__grid"
            onSubmit={() => {}}
            autoComplete="off"
          >
            {renderColumnElements(initialElements)}
            {contractParams.pricingStructure !== "fixed_pricing" &&
              renderColumnElements(conditionalElements)}
            {renderColumnElements(remainingElementsAfterPricingStructure)}
            {contractParams.calculateShortfallOrSurplusCompensation === "yes" &&
              renderColumnElements(conditionlElementsForShortFallOrSurplus)}
            {renderColumnElements(finalElements)}
          </form>
          {/* {title === "Onboard" &&  showModal && <AdminOnBoardingContractModal contractParams={{ ...contractParams}} toggleModal={setShowModal} submitContractOnboarding={submitContractOnboarding}/>} */}
          {title === "Onboard" && showModal && (
            <ConfirmModal
              data={null}
              params={{
                ...contractParams,
                generator: generators.filter(
                  (item) => item.id === contractParams.generator,
                )?.[0]?.clientName,
                offtaker: offtakers.filter(
                  (item) => item.id === contractParams.offtaker,
                )?.[0]?.clientName,
                contractFile: selectedContract?.name,
                paymentFrequency: pascalCase(contractParams.paymentFrequency),
                technologyType: pascalCase(contractParams.technologyType, "_"),
                pricingStructure: pascalCase(
                  contractParams.pricingStructure,
                  "_",
                ),
                escalationFrequency: pascalCase(
                  contractParams.escalationFrequency,
                  "_",
                ),
                deployInBlockchain: pascalCase(
                  contractParams.deployInBlockchain,
                ),
                environmentalAttributeHandling:
                  contractParams.environmentalAttributeHandling
                    ? pascalCase(
                        contractParams.environmentalAttributeHandling,
                        "_",
                      )
                    : "",
              }}
              labels={payloadValidator}
              toggleModal={setShowModal}
              handleSubmit={submitContractOnboarding}
            />
          )}
          {showSuccess ? (
            <SuccessModal
              title="Onboarded"
              message="Contract onboarded successfully!"
              toggleModal={setShowSuccess}
            />
          ) : null}
          <div className="ppa__contract__options">
            <button
              className="forward__option"
              disabled={!contractFormValidity.isFormValid}
              onClick={() => handleSubmit()}
            >
              {title}
            </button>
            <button className="cancel__option" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
